<template>
  <section>
    <div>
      <!-- Modal de onboarding -->
      <onboarding-video ref="modal" :show="showOnboarding"/>
    </div>

    <div v-if="isLoading" class="my-4 text-center">
      <scale-loader></scale-loader>
    </div>
    <div v-else>
      <div v-if="!this.projects || this.projects.length == 0" class="mt-16">
        <p class="text-3xl text-center text-grey">
          <b-icon pack="fas" icon="ban" size="is-large"/>
          {{ $t('no_projects_to_select') }}
        </p>
      </div>
      <template v-else>
        <div>
          <!-- Back link + view toggle -->
          <div class="epc-page-head">
            <div class="epc-breadcrumb">
              <span class="epc-crumb epc-crumb--back" @click="$router.push({ name: 'group.select' })">
                <b-icon pack="fas" icon="angle-left" size="is-small"/>
                {{ $t('back_to') }} {{ $t('groups') }}
              </span>
            </div>

            <div class="epc-viewtoggle">
              <button type="button" class="epc-view-btn epc-view-btn--active">
                <b-icon pack="fas" icon="th-large" size="is-small"/>
                <span>{{ $t('cards_view') }}</span>
              </button>
              <button type="button" class="epc-view-btn" @click="selectGroupMap">
                <b-icon pack="fas" icon="map-marked-alt" size="is-small"/>
                <span>{{ $t('map') }}</span>
              </button>
            </div>
          </div>

          <!-- Toolbar: search + archive -->
          <div class="epc-toolbar">
            <div class="epc-search">
              <span class="epc-search-icon"><b-icon pack="fas" icon="search" size="is-small"/></span>
              <input v-model="search" type="search"
                :placeholder="$t('search_project')"
                class="epc-search-input"
              />
            </div>

            <div class="epc-archive">
              <v-switch
                v-model="showArchive"
                ref="show_archive"
                :label="$t('show_archive')"
                :color="showArchive ? 'green' : 'red'"
                v-on:checked="(value) => showArchive = value"
              />
            </div>
          </div>

          <!-- Projects header -->
          <div class="epc-group-section">
            <div class="epc-group-head">
              <span class="epc-group-code epc-group-code--blue">{{ sectionBadge }}</span>
              <span class="epc-group-name">{{ sectionTitle }}</span>
              <span class="epc-group-count">{{ filteredProjects().length }} {{ $t('projects') }}</span>
            </div>

            <div v-if="filteredProjects().length == 0" class="epc-empty epc-empty--inline">
              <b-icon pack="fas" icon="search" size="is-large"/>
              <p>{{ $t('no_results') }}</p>
            </div>

            <div v-else class="epc-grid">
              <div
                v-for="project in filteredProjects()"
                :key="project.id"
                class="epc-proj-card"
                :class="{ 'epc-proj-card--disabled': !project.can_participate }"
              >
                <div class="epc-proj-img" @click="selectProject(project)">
                  <img v-if="project.image" :src="project.image" :alt="project.name" />
                  <div v-else class="epc-noimg">{{ $t('no_results') }}</div>
                  <div class="epc-img-scrim"></div>

                  <div class="epc-img-top">
                    <span class="epc-chips">
                      <span v-show="project.can_view" class="epc-chip"><b-icon pack="fas" icon="eye" size="is-small"/></span>
                      <span v-show="project.can_collaborate" class="epc-chip"><b-icon pack="fas" icon="address-book" size="is-small"/></span>
                    </span>
                    <span v-if="project.notifications" class="epc-badge">
                      <b-icon pack="fas" icon="bell" size="is-small"/> {{ project.notifications }}
                    </span>
                  </div>

                  <div class="epc-img-bottom">
                    <div v-if="project.reference" class="epc-card-code">{{ project.reference }}</div>
                    <span class="epc-open"><b-icon pack="fas" icon="arrow-right" size="is-small"/></span>
                  </div>
                </div>

                <div class="epc-card-body">
                  <div class="epc-card-title" @click="selectProject(project)">{{ project.name }}</div>

                  <div class="epc-card-foot">
                    <b-dropdown v-show="project.can_participate">
                      <button class="button is-small" type="button" slot="trigger">
                        <template v-if="!project.user_notifications.project_notifications || project.user_notifications.project_notifications === 'all'">
                          <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                          <span class="text-grey">{{ $t('all_notifications') }}</span>
                        </template>
                        <template v-else>
                          <b-icon pack="far" icon="bell" class="float-left h-full text-grey"></b-icon>
                          <span class="text-grey">{{ $t('mentions_favorites') }}</span>
                        </template>
                        <b-icon pack="fas" class="text-grey" icon="angle-down"></b-icon>
                      </button>

                      <b-dropdown-item @click="setProjectNotifications(project, true)" value="all">
                        <div class="media">
                          <b-icon class="media-left" icon="globe-africa"></b-icon>
                          <div class="media-content">
                            <h3>{{ $t('all_notifications') }}</h3>
                            <small>{{ $t('all_notifications_text') }}</small>
                          </div>
                        </div>
                      </b-dropdown-item>

                      <b-dropdown-item @click="setProjectNotifications(project, false)" value="mentions_favorites">
                        <div class="media">
                          <b-icon class="media-left" icon="user-check"></b-icon>
                          <div class="media-content">
                            <h3>{{ $t('mentions_favorites') }}</h3>
                            <small>{{ $t('mentions_favorites_text') }}</small>
                          </div>
                        </div>
                      </b-dropdown-item>
                    </b-dropdown>

                    <span class="epc-open-label" @click="selectProject(project)">
                      {{ $t('open') }} <b-icon pack="fas" icon="angle-right" size="is-small"/>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import store from "~/store";
import { mapGetters } from 'vuex'
import axios from "axios";
import ScaleLoader from "vue-spinner/src/ScaleLoader.vue";
import OnboardingVideo from "../../components/onboarding-video";

export default {
  middleware: "auth",
  title: "select_project_page_title",
  subtitle: "select_project_page_subtitle",
  menuOption: "2-0",
  components: {
    OnboardingVideo,
    ScaleLoader
  },

  computed: {
    showOnboarding() {
      return this.$route.params.showOnboarding && this.$route.params.showOnboarding === "1"
    },
    ...mapGetters({
      company: 'app/company',
    }),
    sectionBadge() {
      if (this.group) {
        return this.group.code || this.group.name
      }
      return this.$t('projects')
    },
    sectionTitle() {
      if (this.group) {
        return this.$t('projects_of_group', [this.group.name])
      }
      return this.$t('select_project_page_subtitle')
    },
  },

  watch: {
    showArchive(value) {
      this.isLoading = true
      this.getProjects()
    }
  },

  data: () => ({
    group: null,
    projects: null,
    isLoading: true,
    search: '',
    showArchive: 0,
  }),

  created() {
    this.group = store.getters["app/group"];
  },

  mounted() {
    this.getProjects();
  },

  methods: {
    async getProjects() {
      const { data } = await axios.get("/api/v2/user/projects", {
        'params' : { showArchive : this.showArchive, group: this.group ? this.group.id : null, companyId: this.company.id, withConfigs: 1 }
      });

      if (data.projects) {
        this.projects = data.projects;
        this.isLoading = false;
      }
    },

    async selectGroupMap() {
      await this.$router.push({ name: "group.select.map" });
    },

    async selectProject(project) {
      await this.$store.dispatch("app/setProject", { project });
      let tags = project.tags
      await this.$store.dispatch("app/setTags", { tags });
      await this.getVisualConfig(project)
      await this.$store.dispatch('app/setDashboardReport', {dashboardReport: {} })
      this.$router.push({ name: "project.home" });
    },

    async setProjectNotifications(project, value) {
      // params
      let params = {
        key: "project_notifications",
        value: value ? "all" : "mentions_favorite",
        project: project.id
      };

      // endpoint
      const { data } = await axios.post(
        "/api/v2/project/user/notifications",
        params
      );

      if (data.success) {
        this.getProjects();
        this.$notify.success("success_change_user_project_notifications");
      } else {
        this.$notify.error("error_change_user_project_notifications");
      }
    },

    filteredProjects() {
      const q = (this.search || '').toLowerCase()
      return this.projects.filter(project => {
        const nameMatch = project.name && project.name.toLowerCase().includes(q)
        const referenceMatch = project.reference && project.reference.toLowerCase().includes(q)
        return !q || nameMatch || referenceMatch
      }).sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }))
    },

    formatPrice(value) {
      let val = (value/1).toFixed(2).replace('.', ',')
      return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    },

    async getVisualConfig(project) {
      if (project.configs) {
        await this.$store.dispatch("app/setVisualConfig", {config: project.configs});
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '#/assets/sass/front/components/project-selection.scss';
</style>
